import { ActionError, Button, Field, Input, cn } from '@westhetics/ui'
import { useId, useState } from 'react'
import { FieldNote, publicField, publicTextAction, touchField } from '@/app/public-screen'
import { supportText } from '@/app/shell'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { isEmail } from '@/lib/masks'
import type { CodeSent, EmailChanged } from './api'

/**
 * Trocar o e-mail confirmado (RN-PAC-55): o e-mail novo só entra depois do código enviado a ele.
 * Dois passos no mesmo lugar: o endereço e "Enviar código"; depois o código e "Confirmar e-mail".
 */

const MESSAGES = {
  email: 'Confira o e-mail: precisa ter @ e um domínio, como nome@exemplo.com.br.',
  codeLength: 'O código tem 6 dígitos. Confira o e-mail e digite todos.',
} as const

export function EmailChange({
  onSend,
  onConfirm,
  onCancel,
  className,
}: {
  onSend: (email: string) => Promise<CodeSent>
  onConfirm: (email: string, code: string) => Promise<EmailChanged>
  onCancel: () => void
  className?: string
}) {
  const id = useId()
  const [email, setEmail] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [sent, setSent] = useState<{ email: string; message: string } | null>(null)
  const [code, setCode] = useState('')
  const [codeTouched, setCodeTouched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const normalized = email.trim().toLowerCase()
  const emailError =
    fieldErrors(error).email ?? (emailTouched && !isEmail(normalized) ? MESSAGES.email : null)
  const codeError =
    fieldErrors(error).code ?? (codeTouched && code.length !== 6 ? MESSAGES.codeLength : null)
  const boxError =
    error && !fieldErrors(error).email && !fieldErrors(error).code ? errorMessage(error) : null

  async function send() {
    setEmailTouched(true)
    if (!isEmail(normalized) || busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await onSend(normalized)
      setSent({ email: normalized, message: result.message })
      setCode('')
      setCodeTouched(false)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  async function confirm() {
    setCodeTouched(true)
    if (!sent || code.length !== 6 || busy) return
    setBusy(true)
    setError(null)
    try {
      await onConfirm(sent.email, code)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={cn('border-hairline mt-4 border-t pt-5', className)}>
      {!sent ? (
        <>
          <Field id={`${id}-email`} label="E-mail novo" error={emailError ?? undefined}>
            <Input
              value={email}
              className={touchField}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={254}
              onChange={(event) => {
                setEmail(event.target.value)
                setError(null)
              }}
              onBlur={() => setEmailTouched(true)}
            />
          </Field>
          <p className={cn(supportText, 'mt-3')}>
            Enviamos um código para o e-mail novo. Ele só entra no cadastro depois de confirmado.
          </p>
          <Button
            type="button"
            size="lg"
            className="mt-5"
            loading={busy}
            loadingLabel="Enviando…"
            onClick={() => void send()}
          >
            Enviar código
          </Button>
        </>
      ) : (
        <>
          <p className={cn(supportText, 'break-words')}>
            {sent.message} E-mail novo: <span className="text-ink">{sent.email}</span>
          </p>
          <label htmlFor={`${id}-code`} className="eyebrow mt-6 block">
            Código
          </label>
          <input
            id={`${id}-code`}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            value={code}
            aria-invalid={codeError ? true : undefined}
            aria-describedby={codeError ? `${id}-code-note` : undefined}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
              setError(null)
            }}
            onBlur={() => setCodeTouched(true)}
            className={cn(publicField, 'font-display text-[18px] tracking-[.3em]')}
          />
          <FieldNote id={`${id}-code-note`} error={codeError} />
          <Button
            type="button"
            size="lg"
            className="mt-6"
            loading={busy}
            loadingLabel="Confirmando…"
            onClick={() => void confirm()}
          >
            Confirmar e-mail
          </Button>
        </>
      )}
      {boxError ? (
        <ActionError
          className="mt-5"
          title="Não foi possível trocar o e-mail"
          description={boxError}
        />
      ) : null}
      <button type="button" onClick={onCancel} className={cn(publicTextAction, 'mt-5')}>
        Manter o e-mail confirmado
      </button>
    </div>
  )
}
